import { useSubscription } from "@apollo/client";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { SUBSCRIPTION_ON_ERROR_LIVE } from "../../../graphql/live/subscription";
import { convertNumberToWords, convertThaiToLao } from "../../../helpers";
import { useSpeechSynthesis } from 'react-speech-kit';
import Swal from "sweetalert2";
import { io } from "socket.io-client";
import { convertTextLaoToThai, isLaoLanguage } from "../../../helpers/textLaosToThai";
import { K_LOCAL_STORAGE_SHOP_SETTING_DATA, SERVER_URI, SOCKET_SERVER_URI, TEXT_SERVICES } from "../../../constants";
import _ from 'lodash';

export function LiveErrorScreen({ liveData, playNoProduct }) {
    const soundPlayer = useRef(null);
    const soundPlayerCloud = useRef(null);
    const socketRef = useRef(null);
    const [_voices, setVoices] = useState([]);
    const [setting, setSetting] = useState({});
    // Add this state manual play audio local
    const [pendingAudioUrl, setPendingAudioUrl] = useState(null);
    const audioButtonRef = useRef(null);
    // Add this state manual play audio cloud
    const [pendingAudioUrlCloud, setPendingAudioUrlCloud] = useState(null);
    const audioButtonRefCloud = useRef(null);

    const liveId = liveData?.id;
    const shopId = localStorage.getItem("SHOP");
    const { speak } = useSpeechSynthesis();

    const { data } = useSubscription(SUBSCRIPTION_ON_ERROR_LIVE, {
        variables: {
            liveId: liveData?.id
        }
    });

    // ... existing code ...
    const [audioContext, setAudioContext] = useState(null);
    
    // Initialize audio context on component mount
    useEffect(() => {
        // Create audio context
        const context = new (window.AudioContext || window.webkitAudioContext)();
        setAudioContext(context);
        
        // Resume context on user interaction
        const resumeAudioContext = () => {
            if (context.state === 'suspended') {
                context.resume();
            }
        };
        
        // Add event listeners for user interaction
        document.addEventListener('click', resumeAudioContext);
        document.addEventListener('touchstart', resumeAudioContext);
        document.addEventListener('keydown', resumeAudioContext);

        function setSpeechVoices() {
            setVoices(window.speechSynthesis.getVoices());
        }

        setSpeechVoices();
        if (speechSynthesis.onvoiceschanged !== undefined) {
            speechSynthesis.onvoiceschanged = setSpeechVoices;
        }

        return () => {
            // Clean up event listeners
            document.removeEventListener('click', resumeAudioContext);
            document.removeEventListener('touchstart', resumeAudioContext);
            document.removeEventListener('keydown', resumeAudioContext);
            
            // Close audio context
            if (context) {
                context.close();
            }
        };
    }, []);

    useEffect(() => {
        if (!setting?.id) {
            const settingData = localStorage.getItem(K_LOCAL_STORAGE_SHOP_SETTING_DATA);
            if (settingData) {
                setSetting(JSON.parse(settingData));
            }
        }
    }, [setting]);

    useEffect(() => {
        if (data) {
            if (data.onErrorLive.code === "LIVE_AMOUNT_IS_LESS_THAN_0") {
                if (playNoProduct){
                    // If use cloud text to speech
                    if (setting?.isLiveSiriVoice === true){
                        console.log("use cloud text to speech");
                        notifyStockEmpty(data?.onErrorLive?.data);
                    } else {
                        const thaiVoice = _voices.find(voice => voice.lang === 'th-TH');
                        if (!thaiVoice) {
                            // check text to speech cloud
                            console.log("check text to speech cloud");
                            notifyStockEmpty(data?.onErrorLive?.data);
                        } else {
                            console.log("check text to speech local");
                            supSpeak();
                        }
                    }
                    // supSpeak();
                }
                Swal.fire({
                    position: "center",
                    icon: "warning",
                    title: `ລະຫັດ: ${data?.onErrorLive?.data} ຫມົດແລ້ວ`,
                    showConfirmButton: false,
                    timer: 5800
                });
            }
        }
        return () => {}
    }, [data]);

    // Extract the error data from props
    const errorData = data?.onErrorLive?.data;

    // Store audio play request instead of playing directly
    const supSpeak = useCallback(() => {
        const thaiVoice = _voices.find(voice => voice.lang === 'th-TH');

        if (!thaiVoice) {
            setPendingAudioUrl('/product_empty.ogg');
            
            // Try to automatically click the button
            setTimeout(() => {
                if (audioButtonRef.current) {
                    audioButtonRef.current.click();
                }
            }, 100);
            return;
        }

        if (!errorData) {
            console.warn('No error data available for text-to-speech');
            return;
        }

        const thaiText = convertThaiToLao(errorData);
        // Get saved rate or use default
        const savedRate = parseFloat(localStorage.getItem('speechRate')) || 0.9;
        let responseText = `รหัส ${convertNumberToWords(thaiText)} หมดแล้วค่ะ`;
        speak({ text: responseText, rate: savedRate, voice: thaiVoice });
    }, [errorData, _voices, speak, setPendingAudioUrl]);

    const socketConnectionState = useRef('DISCONNECTED'); // DISCONNECTED, CONNECTING, CONNECTED

    const closeSocket = useCallback(() => {
        if (socketRef.current) {
            console.log("Closing socket connection.");
            socketRef.current.disconnect();
        }
    }, []);

    // Initialize socket once on mount
    useEffect(() => {
        // const isLocal = SERVER_URI?.startsWith('http://localhost');
        // socketRef.current = io(isLocal ? SERVER_URI : SOCKET_SERVER_URI, {
        //     autoConnect: false,
        //     transports: isLocal ? ['polling'] : ['websocket', 'polling'],
        //     upgrade: !isLocal,
        //     timeout: 5000,
        //     reconnection: true,
        //     reconnectionAttempts: 3,
        //     reconnectionDelay: 1000,
        // });
        socketRef.current = io(SOCKET_SERVER_URI, { autoConnect: false });
        socketRef.current.on('connect_error', () => {
            console.log("connect_error");
            if (socketRef.current?.io?.opts) {
                socketRef.current.io.opts.transports = ['polling'];
                socketRef.current.io.opts.upgrade = false;
                socketRef.current.connect();
            }
        });

        return () => {
            if (socketRef.current) {
                console.log("Component unmounting, disconnecting socket.");
                closeSocket();
                socketRef.current = null;
            }
        };
    }, [closeSocket]);

    // Manage socket event listeners
    useEffect(() => {
        if (!socketRef.current) return;

        const onConnect = () => {
            // console.log('Socket connected successfully');
            socketConnectionState.current = 'CONNECTED';
            socketRef.current.emit("join-live", liveId);
        };

        const onConnectError = (error) => {
            // console.error('Socket connection error:', error);
            socketConnectionState.current = 'DISCONNECTED';
            supSpeak(); // Fallback
        };

        const onDisconnect = (reason) => {
            // console.log('Socket disconnected:', reason);
            socketConnectionState.current = 'DISCONNECTED';
        };

        const onError = (error) => {
            console.error('Socket error:', error);
        };

        const handleTtsAudio = (data) => {
            // console.log("Received TTS audio data: ", data);
            if (data?.status && data?.liveId === liveId && data?.services === TEXT_SERVICES[0]) {
                try {
                    if (!data?.audioContent) {
                        console.log("No audio content received");
                        supSpeak();
                        return;
                    }

                    if (audioContext && audioContext.state === 'running') {
                        const base64 = data.audioContent;
                        const binaryString = window.atob(base64);
                        const len = binaryString.length;
                        const bytes = new Uint8Array(len);
                        for (let i = 0; i < len; i++) {
                            bytes[i] = binaryString.charCodeAt(i);
                        }
                        
                        audioContext.decodeAudioData(bytes.buffer, 
                            (buffer) => {
                                const source = audioContext.createBufferSource();
                                source.buffer = buffer;
                                source.connect(audioContext.destination);
                                source.start(0);
                                console.log('Audio playback started successfully with Web Audio API');
                            },
                            (error) => {
                                console.error('Error decoding audio data:', error);
                                fallbackToHtmlAudio(data.audioContent);
                            }
                        );
                    } else {
                        fallbackToHtmlAudio(data.audioContent);
                    }

                    localStorage.setItem("TEXT_SERVICES", JSON.stringify({cloud: true, service: TEXT_SERVICES[0]}));
                } catch (error) {
                    console.error("Error playing audio:", error);
                    supSpeak();
                } finally {
                    closeSocket();
                }
            } else {
                if (_.isEmpty(data?.services)) {
                    localStorage.setItem("TEXT_SERVICES_ERROR", JSON.stringify(data));
                    closeSocket();
                    supSpeak();
                }
            }
        };

        const fallbackToHtmlAudio = (audioContent) => {
            const audioUrl = `data:audio/mp3;base64,${audioContent}`;
            setPendingAudioUrlCloud(audioUrl);
            if (audioButtonRefCloud.current) {
                audioButtonRefCloud.current.click();
            }
        };

        socketRef.current.on('connect', onConnect);
        socketRef.current.on('connect_error', onConnectError);
        socketRef.current.on('disconnect', onDisconnect);
        socketRef.current.on('error', onError);
        socketRef.current.on("tts-audio", handleTtsAudio);

        return () => {
            if (socketRef.current) {
                socketRef.current.off('connect', onConnect);
                socketRef.current.off('connect_error', onConnectError);
                socketRef.current.off('disconnect', onDisconnect);
                socketRef.current.off('error', onError);
                socketRef.current.off("tts-audio", handleTtsAudio);
            }
        };
    }, [liveId, audioContext, closeSocket, supSpeak]);

    const notifyStockEmpty = (text) => {
        if (!liveId) {
            console.error("Live ID is missing");
            supSpeak();
            return;
        }

        const languageLaos = isLaoLanguage(text);
        if (!languageLaos) console.log("Text language could not be identified.");
        const newText = languageLaos ? convertTextLaoToThai(text) : text;

        const emitStockEmpty = () => {
            // console.log("Emitting stock-empty");
            socketRef.current.emit("stock-empty", {
                text: `รหัส ${newText} หมดแล้วค่ะ`,
                liveId,
                services: "GOOGLE_CLOUD",
                shop: shopId,
            });
        };

        if (!socketRef.current) {
            console.error("Socket not initialized");
            return;
        }

        if (socketConnectionState.current === 'CONNECTED') {
            emitStockEmpty();
        } else if (socketConnectionState.current === 'DISCONNECTED') {
            // console.log("Socket is disconnected, attempting to connect...");
            socketConnectionState.current = 'CONNECTING';
            socketRef.current.connect();
            // The 'connect' event listener will handle the emit
            socketRef.current.once('connect', () => {
                 // Re-join live and then emit
                socketRef.current.emit("join-live", liveId);
                emitStockEmpty();
            });
        } else {
            // If 'CONNECTING', wait for connection
            socketRef.current.once('connect', () => {
                emitStockEmpty();
            });
        }
    };

    return (
    <>
        <audio ref={soundPlayer} preload="metadata" />
        <audio ref={soundPlayerCloud} preload="metadata" />

        {/* Hidden play button that can be programmatically clicked */}
        <button 
            ref={audioButtonRef}
            style={{ position: 'fixed', bottom: 0, right: 0, opacity: 0.1, zIndex: 9999 }}
            onClick={() => {
                if (pendingAudioUrl) {
                    soundPlayer.current.src = pendingAudioUrl;
                    soundPlayer.current.load();
                    soundPlayer.current.play()
                        .then(() => {
                            console.log('Audio played successfully via button click');
                            setPendingAudioUrl(null);
                        })
                        .catch(err => {
                            console.error('Audio play failed even with button:', err);
                            supSpeak();
                        });
                }
            }}
        >
        </button>

        {/* Visible play button for user interaction */}
        <button 
            ref={audioButtonRefCloud}
            style={{ position: 'fixed', bottom: 0, right: 0, opacity: 0.1, zIndex: 9999 }}
            onClick={() => {
                if (pendingAudioUrlCloud) {
                    soundPlayerCloud.current.src = pendingAudioUrlCloud;
                    soundPlayerCloud.current.load();
                    soundPlayerCloud.current.play()
                        .then(() => {
                            console.log('Audio played successfully via user click');
                            setPendingAudioUrlCloud(null);
                        })
                        .catch(err => {
                            console.error('Audio play failed even with user click:', err);
                            supSpeak();
                        });
                }
            }}
        >
        </button>
    </>
    );
}